/* Product Studio overlay (video and embeds) and loop clips. Ships to the theme next to product-studio.css; loaded once per page.
   Enhances <a class="ps__play" href="…" data-video="…mp4">, data-youtube="ID"> or data-embed="https://…">, and
   <a class="ps__zoom" href="…"> on an image box, which shows the picture at the window's size; without this script
   the link opens the video, the page or the file directly. The overlay has no words of its own: its title is
   the link's aria-label, a zoomed picture's alt is the tile's, and its close button is named by the link's
   data-close-label, so the theme's locale file is the one place the storefront's words live. An embed loads nothing from its host until pressed. Plays <video class="ps__clip"> while its tile is on screen; without it the poster stays. */
(() => {
  let dlg, frame;

  function ensure() {
    if (dlg) return dlg;
    dlg = document.createElement('dialog');
    dlg.className = 'ps-player';
    dlg.innerHTML = '<button type="button" class="ps-player__close"><svg viewBox="0 0 256 256" aria-hidden="true"><path d="M208.49,191.51a12,12,0,0,1-17,17L128,145,64.49,208.49a12,12,0,0,1-17-17L111,128,47.51,64.49a12,12,0,0,1,17-17L128,111l63.51-63.52a12,12,0,0,1,17,17L145,128Z"/></svg></button><div class="ps-player__frame"></div>';   // the cross is the registry's x, pasted from src/icons.js since the page carries no sprite; render.test.js holds it to the registry
    frame = dlg.querySelector('.ps-player__frame');
    dlg.addEventListener('click', (e) => { if (e.target === dlg || e.target.closest('.ps-player__close')) dlg.close(); });
    dlg.addEventListener('close', () => { frame.innerHTML = ''; frame.classList.remove('is-loaded'); frame.style.backgroundImage = ''; });   // stops playback and drops the iframe
    document.body.appendChild(dlg);
    return dlg;
  }

  function open(source, { title = '', close = '' } = {}) {
    // YouTube refuses embeds without a referrer (error 153), which a page opened from disk or as a blob cannot send
    if (source.youtube && !/^https?:$/.test(location.protocol)) {
      window.open(`https://www.youtube.com/watch?v=${encodeURIComponent(source.youtube)}`, '_blank', 'noopener');
      return;
    }
    ensure();
    dlg.querySelector('.ps-player__close').setAttribute('aria-label', close);
    const esc = (v) => String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    const t = esc(title);
    dlg.classList.toggle('ps-player--embed', !!source.embed);
    dlg.classList.toggle('ps-player--image', !!source.image);
    if (source.image) {
      frame.innerHTML = `<img src="${esc(source.image)}" alt="${esc(source.alt || '')}">`;
      dlg.showModal();
      return;
    }
    if (source.embed) {
      let src = source.embed;
      if (reduce.matches) { try { const u = new URL(src); u.searchParams.delete('autospin'); src = u.href; } catch { /* used as given */ } }
      frame.style.backgroundImage = source.poster ? `url("${String(source.poster).replace(/["\\]/g, '')}")` : '';
      frame.innerHTML = `<iframe src="${esc(src)}" title="${t}" allow="autoplay; fullscreen; xr-spatial-tracking" allowfullscreen referrerpolicy="strict-origin-when-cross-origin"></iframe>`;
      frame.firstChild.addEventListener('load', () => frame.classList.add('is-loaded'), { once: true });   // the poster shows until then
      dlg.showModal();
      return;
    }
    frame.innerHTML = source.youtube
      ? `<iframe src="https://www.youtube-nocookie.com/embed/${encodeURIComponent(source.youtube)}?autoplay=1&rel=0" title="${t}" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; web-share" allowfullscreen></iframe>`
      : `<video src="${source.video.replace(/"/g, '&quot;')}" controls autoplay playsinline></video>`;
    dlg.showModal();
  }

  document.addEventListener('click', (e) => {
    const a = e.target.closest('a.ps__play[data-video], a.ps__play[data-youtube], a.ps__play[data-embed], a.ps__zoom');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    const source = a.classList.contains('ps__zoom') ? { image: a.href, alt: a.closest('.ps__box')?.querySelector('img')?.alt || '' }
      : a.dataset.embed
      ? { embed: a.dataset.embed, poster: a.closest('.ps__box')?.querySelector('img.ps__bg')?.currentSrc || '' }
      : a.dataset.youtube ? { youtube: a.dataset.youtube } : { video: a.dataset.video };
    open(source, { title: a.getAttribute('aria-label'), close: a.dataset.closeLabel });
  });

  // an embed's host is warmed up as the pointer arrives, so the press feels quicker; once per origin
  const warmed = new Set();
  const warm = (e) => {
    const a = e.target.closest?.('a.ps__play[data-embed]');
    if (!a) return;
    let origin;
    try { origin = new URL(a.dataset.embed).origin; } catch { return; }
    if (warmed.has(origin)) return;
    warmed.add(origin);
    const link = document.createElement('link');
    link.rel = 'preconnect'; link.href = origin; link.crossOrigin = '';
    document.head.appendChild(link);
  };
  document.addEventListener('pointerover', warm);
  document.addEventListener('focusin', warm);

  // Loop clips: on screen and not under reduced motion, play; off screen, pause; a tap on the tile pauses and resumes.
  // watch() can be called again for clips added later (the dashboard re-renders its preview).
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  let io, tapBound = false;
  function watch(root = document, { tap = true } = {}) {
    const clips = root.querySelectorAll('video.ps__clip:not([data-watched])');
    if (!clips.length) return;
    if (!('IntersectionObserver' in window)) return;   // old browsers keep the poster
    io = io || new IntersectionObserver((entries) => entries.forEach(({ target: v, isIntersecting }) => {
      if (isIntersecting && !reduce.matches && !v.dataset.paused) v.play().catch(() => {});
      else v.pause();
    }), { threshold: 0.25 });
    clips.forEach((v) => { v.dataset.watched = ''; io.observe(v); });
    if (tap && !tapBound) {
      tapBound = true;
      document.addEventListener('click', (e) => {
        if (e.target.closest('a, button')) return;
        const v = e.target.closest('.ps__box')?.querySelector('video.ps__clip');
        if (!v) return;
        if (v.paused) { delete v.dataset.paused; v.play().catch(() => {}); } else { v.dataset.paused = ''; v.pause(); }
      });
    }
  }
  // start after the load event, so no clip is requested until every image, font and script has arrived
  if (document.readyState === 'complete') watch(); else window.addEventListener('load', () => watch());

  window.ProductStudioPlayer = { open, watch };
})();
